/**
 * TYPES
 */
import * as Events from 'src/aggregates/conversations/events.d';
import {IConversationOnlineAgentsLoading}
    from 'src/aggregates/conversations/events.d';
import {types} from 'src/aggregates/conversations/events.d';
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {ConversationDynamic} from 'src/aggregates/conversations/mutations.d';
import {IAnalyzedConversation} from 'src/aggregates/conversations/mutations.d';
import {IRejectedConversation} from 'src/aggregates/conversations/mutations.d';
import {IAgentSession} from 'src/aggregates/conversations/queries.d';
import {IOngoingConversation} from 'src/aggregates/conversations/queries.d';
import {IPush} from 'src/aggregates/conversations/session/session.d';
import {IConversation} from 'src/aggregates/conversations/state.d';
import {IConversationHistory} from 'src/aggregates/conversations/state.d';
import {IMessage} from 'src/aggregates/conversations/state.d';
import {IMetricsFilters} from 'src/aggregates/conversations/state.d';
import {IQualityConversation} from 'src/aggregates/conversations/state.d';
import {IQualityMetadata} from 'src/aggregates/conversations/state.d';
import {IRealTimeMetrics} from 'src/aggregates/conversations/state.d';
import {IConversationsMetrics} from 'src/aggregates/conversations/utils.d';
import {IMessagesMetricsByAgent} from 'src/aggregates/conversations/utils.d';
import {_aggregates} from 'src/aggregates/index.d';
import {IEventCreator} from 'src/aggregates/index.d';
import {IAttachment} from 'src/aggregates/timelines/state.d';
import {IReferral} from 'src/aggregates/timelines/state.d';
import {IMessage as ITimelineMessage} from 'src/aggregates/timelines/state.d';
import {ToastTypes} from 'src/aggregates/utils/feedback';
import {IMessage as IComponentMessage}
    from 'src/components/Shared/Message/index.d';


/**
 * CODE
 */

/**
 * I create a conversation added event.
 *
 * :param account: conversation account id
 * :param channel: conversation channel
 * :param customer: conversation customer
 * :param end: conversation end timestamp
 * :param hasAgentAnswer: has agent answer
 * :param history: updated history array
 * :param id: conversation id
 * :param lastInteraction: conversation last interaction
 * :param messages: conversation messages
 * :param ongoing: updated ongoing array
 * :param remote: conversation remote
 * :param start: conversation start
 * :param subject: conversation subject
 * :param unansweredMessages: conversation unanswered messages
 * :param customer: customer id
 * :param dynamic: dynamic data
 *
 * :returns: conversation added event
 */
function conversationAdded (
    account: number,
    channel: string,
    end: number | null,
    hasAgentAnswer: boolean | null,
    history: (number | string)[],
    id: number,
    isSendingTemplate: boolean,
    lastInteraction,
    messages: object,
    ongoing: (number | string)[],
    remote: string,
    start: number,
    subject: string | null,
    templateRequired: boolean,
    templateSent: number | null,
    unansweredMessages: number,
    customer?: string | number,
    dynamic?: ConversationDynamic | null,
): Events.IConversationAdded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ADDED,
        _target: id,
        account,
        channel,
        customer,
        dynamic,
        end,
        hasAgentAnswer,
        history,
        isSendingTemplate,
        lastInteraction,
        messages,
        ongoing,
        remote,
        start,
        subject,
        templateRequired,
        templateSent,
        type: types.CONVERSATION_ADDED,
        unansweredMessages,
    };
}


/**
 * I create a conversation agents messages metrics fetched event.
 *
 * :param metrics: messages metrics
 * :param start: start date
 * :param end: end date
 * :param filters: metrics filters
 *
 * :returns: conversation agents messages metrics fetched event
 */
function conversationAgentsMessagesMetricsFetched (
    metrics: IMessagesMetricsByAgent,
    start: number,
    end: number,
    filters?: IMetricsFilters,
): Events.IConversationAgentsMessagesMetricsFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_AGENTS_MESSAGES_METRICS_FETCHED,
        _target: null,
        end,
        filters,
        metrics,
        start,
        type: types.CONVERSATION_AGENTS_MESSAGES_METRICS_FETCHED,
    };
}


/**
 * I create a conversation agents messages metrics fetching event.
 *
 * :returns: conversation agents messages metrics fetching event
 */
function conversationAgentsMessagesMetricsFetching
(): Events.IConversationAgentsMessagesMetricsFetching
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_AGENTS_MESSAGES_METRICS_FETCHING,
        _target: null,
        type: types.CONVERSATION_AGENTS_MESSAGES_METRICS_FETCHING,
    };
}


/**
 * I create a conversation agents messages metrics not fetched event.
 *
 * :returns: conversation agents messages metrics not fetched event
 */
function conversationAgentsMessagesMetricsNotFetched
(): Events.IConversationAgentsMessagesMetricsNotFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_AGENTS_MESSAGES_METRICS_NOT_FETCHED,
        _target: null,
        type: types.CONVERSATION_AGENTS_MESSAGES_METRICS_NOT_FETCHED,
    };
}


/**
 * I create a conversation all pages loaded event.
 *
 * :returns: conversation page loaded event
 */
function conversationAllPagesLoaded(): Events.IConversationAllPagesLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ALL_PAGES_LOADED,
        _target: null,
        type: types.CONVERSATION_ALL_PAGES_LOADED,
    };
}


/**
 * I create an event when conversation already exists.
 *
 * :param id: conversation id
 *
 * :returns: already exists event
 */
const conversationAlreadyExists:
    IEventCreator<Events.IConversationAlreadyExists> =
    (id: number): Events.IConversationAlreadyExists => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ALREADY_EXISTS,
        _target: id,
        type: types.CONVERSATION_ALREADY_EXISTS,
    });


/**
     * I create a conversation auto answered event.
     *
     * :param id: conversation id
     *
     * :returns: isAutoAnswered event
     */
function conversationAutoAnswered(
    id: number,
): Events.IConversationAutoAnswered
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_AUTOANSWERED,
        _target: id,
        type: types.CONVERSATION_AUTOANSWERED,
    };
}


/**
 * I create a conversation created event.
 *
 * :param id: conversation id
 *
 * :returns: conversation created event
 */
const conversationCreated: IEventCreator<Events.IConversationCreated> =
    (id: number): Events.IConversationCreated => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_CREATED,
        _target: id,
        type: types.CONVERSATION_CREATED,
    });


/**
 * I create a conversation creating event.
 *
 * :returns: conversation creating event
 */
const conversationCreating: IEventCreator<Events.IConversationCreating> =
    (): Events.IConversationCreating => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_CREATING,
        _target: null,
        type: types.CONVERSATION_CREATING,
    });


/**
 * I create an error reset event.
 *
 * :returns: error reset event
 */
const conversationErrorReset: IEventCreator<Events.IConversationErrorReset> =
    (): Events.IConversationErrorReset => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ERROR_RESET,
        _target: null,
        type: types.CONVERSATION_ERROR_RESET,
    });


/**
 * I create an event when a conversation exited an agent.
 *
 * :param id: conversation id
 * :param status: conversation exit status
 *
 * :returns: conversation exited event
 */
const conversationExited:
    IEventCreator<Events.IConversationExited> =
    (id: number, status: number): Events.IConversationExited => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_EXITED,
        _target: id,
        status,
        type: types.CONVERSATION_EXITED,
    });


/**
 * I create a feedback added event.
 *
 * :param closable: whether toast is closable or not
 * :param duration: toast duration
 * :param messag: toast message
 * :param title: toast title
 * :param toastType: toast type
 *
 * :returns: feedback added event
 */
const conversationFeedbackAdded:
    IEventCreator<Events.IConversationFeedbackAdded> =
        (
            closable: boolean,
            duration: number,
            message: string,
            title: string,
            toastType: ToastTypes,
        ): Events.IConversationFeedbackAdded => ({
            _aggregate: _aggregates.CONVERSATIONS,
            _name: types.CONVERSATION_FEEDBACK_ADDED,
            _target: null,
            closable,
            duration,
            message,
            title,
            toastType,
            type: types.CONVERSATION_FEEDBACK_ADDED,
        });


/*
 * I create a feedback removed event.
 *
 * :param index: feedback index
 *
 * :returns: feedback removed event
 */
const conversationFeedbackRemoved:
    IEventCreator<Events.IConversationFeedbackRemoved> =
        (index: number): Events.IConversationFeedbackRemoved => ({
            _aggregate: _aggregates.CONVERSATIONS,
            _name: types.CONVERSATION_FEEDBACK_REMOVED,
            _target: null,
            index,
            type: types.CONVERSATION_FEEDBACK_REMOVED,
        });


/**
 * I create a conversation fetched event.
 *
 * :param id: conversation id
 *
 * :returns: conversation fetched event
 */
const conversationFetched: IEventCreator<Events.IConversationFetched> =
    (
        id: number,
        messages: ITimelineMessage[],
        hasAgentAnswer?: boolean,
    ): Events.IConversationFetched => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FETCHED,
        _target: id,
        hasAgentAnswer: hasAgentAnswer,
        messages,
        type: types.CONVERSATION_FETCHED,
    });


/**
 * I create a conversation-fetched-by-ids event.
 *
 * :returns: conversation-fetched-by-ids event
 */
const conversationFetchedByIds: IEventCreator<
    Events.IConversationFetchedByIds
> = (): Events.IConversationFetchedByIds => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_FETCHED_BY_IDS,
    _target: null,
    type: types.CONVERSATION_FETCHED_BY_IDS,
});


/**
 * I create a conversation fetching event.
 *
 * :param id: conversation id
 *
 * :returns: conversation fetching event
 */
const conversationFetching: IEventCreator<Events.IConversationFetching> = (
    id: number,
): Events.IConversationFetching => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_FETCHING,
    _target: id,
    type: types.CONVERSATION_FETCHING,
});


/**
 * I create a conversation-fetching-by-ids event.
 *
 * :returns: conversation-fetching-by-ids event
 */
const conversationFetchingByIds: IEventCreator<
    Events.IConversationFetchingByIds
> = (): Events.IConversationFetchingByIds => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_FETCHING_BY_IDS,
    _target: null,
    type: types.CONVERSATION_FETCHING_BY_IDS,
});


/**
 * I create an file not uploaded event.
 *
 * :param id: conversation id
 * :param message: message original id
 *
 * :returns: file not uploaded event
 */
const conversationFileNotUploaded:
    IEventCreator<Events.IConversationFileNotUploaded> =
    (id: number, message: string): Events.IConversationFileNotUploaded => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FILE_NOT_UPLOADED,
        _target: id,
        message,
        type: types.CONVERSATION_FILE_NOT_UPLOADED,
    });


/**
 * I create a file uploaded event.
 *
 * :param id: conversation id
 * :param message: message original id
 *
 * :returns: file uploaded event
 */
const conversationFileUploaded:
    IEventCreator<Events.IConversationFileUploaded> =
    (id: number, message: string): Events.IConversationFileUploaded => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FILE_UPLOADED,
        _target: id,
        message,
        type: types.CONVERSATION_FILE_UPLOADED,
    });


/**
 * I create a file uploading event.
 *
 * :param id: conversation id
 * :param message: message original id
 * :param progress: percentage of progress
 *
 * :returns: file uploading event
 */
const conversationFileUploading:
    IEventCreator<Events.IConversationFileUploading> =
    (id: number,
     message: string,
     progress: number): Events.IConversationFileUploading => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FILE_UPLOADING,
        _target: id,
        message,
        progress,
        type: types.CONVERSATION_FILE_UPLOADING,
    });


/**
 * I create a conversation finished event.
 *
 * :param conversations: conversations ids
 *
 * :returns: conversation finished event
 */
function conversationFinished (
    conversations: number[],
): Events.IConversationFinished
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FINISHED,
        _target: null,
        conversations,
        type: types.CONVERSATION_FINISHED,
    };
}


/**
 * I create a conversation finishing event.
 *
* :param conversations: conversations ids
 *
 * :returns: conversation finishing event
 */
function conversationFinishing (
    conversations: number[],
): Events.IConversationFinishing
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FINISHING,
        _target: null,
        conversations,
        type: types.CONVERSATION_FINISHING,
    };
}


/**
 * I create a conversation focused event.
 *
 * :param current: conversation id
 * :param currentAccount: account id
 * :param currentChannel: channel id (contactId_accountId)
 * :param currentContact: contact id
 *
 * :returns: conversation focused event
 */
function conversationFocused (
    current: string | number | null,
    currentAccount: number | null,
    currentChannel: string,
    currentContact: number | string | null,
): Events.IConversationFocused
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FOCUSED,
        _target: (typeof current === 'number') ? current : null,
        current,
        currentAccount,
        currentChannel,
        currentContact,
        type: types.CONVERSATION_FOCUSED,
    };
}


/**
 * I create a conversation forward started event.
 *
 * :returns: conversation forward started event
 */
function conversationForwardStarted (): Events.IConversationForwardStarted
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FORWARD_STARTED,
        _target: null,
        type: types.CONVERSATION_FORWARD_STARTED,
    };
}


/**
 * I create a conversation forward stopped event.
 *
 * :returns: conversation forward stopped event
 */
function conversationForwardStopped (): Events.IConversationForwardStopped
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FORWARD_STOPPED,
        _target: null,
        type: types.CONVERSATION_FORWARD_STOPPED,
    };
}


/**
 * I create a conversation forwarding message added event.
 *
 * :param message: message to forward
 *
 * :returns: conversation forwarding message added event
 */
function conversationForwardingMessageAdded (
    message: IComponentMessage,
): Events.IConversationForwardingMessageAdded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FORWARDING_MESSAGE_ADDED,
        _target: null,
        message,
        type: types.CONVERSATION_FORWARDING_MESSAGE_ADDED,
    };
}


/**
 * I create a conversation forwarding message removed event.
 *
 *  :param message: message id to remove
 *
 * :returns: conversation forwarding message removed event
 */
function conversationForwardingMessageRemoved (
    message: number,
): Events.IConversationForwardingMessageRemoved
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_FORWARDING_MESSAGE_REMOVED,
        _target: null,
        message,
        type: types.CONVERSATION_FORWARDING_MESSAGE_REMOVED,
    };
}


/**
 * I create a conversation heartbeat succeeded event.
 *
 * :param conversationsOnQueue: number of conversations on queue
 *
 * :returns: conversation heartbeat succeeded event
 */
function conversationHeartbeatSucceeded (
    conversationsOnQueue: number,
): Events.IConversationHeartbeatSucceeded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_HEARTBEAT_SUCCEEDED,
        _target: null,
        conversationsOnQueue,
        type: types.CONVERSATION_HEARTBEAT_SUCCEEDED,
    };
}


/**
 * I create a conversation history added event.
 *
 * :param conversations: conversations data
 *
 * :returns: conversation history added event
 */
function conversationHistoryAdded (
    conversations: IConversationHistory[],
): Events.IConversationHistoryAdded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_HISTORY_ADDED,
        _target: null,
        conversations,
        type: types.CONVERSATION_HISTORY_ADDED,
    };
}


/**
 * I create a conversation loaded event.
 *
 * :param conversations: loaded conversations
 * :param ongoing: ordered customer ids with ongoing conversations
 *
 * :returns: conversation loaded event
 */
function conversationLoaded (
    conversations: IOngoingConversation[],
    ongoing: (number | string)[],
): Events.IConversationLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADED,
        _target: null,
        conversations,
        ongoing,
        type: types.CONVERSATION_LOADED,
    };
}


/**
 * I create a conversation loaded by customer event.
 *
 * :param customer: customer id
 * :param newIds: new conversation ids to be added
 *
 * :returns: conversation loaded by customer event
 */
function conversationLoadedByCustomer (
    customer: number | string,
    newIds: IConversation['id'][],
): Events.IConversationLoadedByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADED_BY_CUSTOMER,
        _target: null,
        customer,
        newIds,
        type: types.CONVERSATION_LOADED_BY_CUSTOMER,
    };
}


/**
 * I create a conversation loaded by period event.
 *
 * :param agent: agent id
 * :param newIds: ids of conversations not yet in the store
 * :param since: oldest timestamp loaded
 *
 * :returns: conversation loaded by period event
 */
function conversationLoadedByPeriod (
    agent: string,
    newIds: number[],
    since: number,
): Events.IConversationLoadedByPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADED_BY_PERIOD,
        _target: null,
        agent,
        newIds,
        since,
        type: types.CONVERSATION_LOADED_BY_PERIOD,
    };
}


/**
 * I create a conversation loaded all by period event.
 *
 * :param agent: agent id
 * :param since: oldest timestamp loaded
 *
 * :returns: conversation loaded all by period event
 */
function conversationLoadedAllByPeriod (
    agent: string,
    since: number,
): Events.IConversationLoadedAllByPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADED_ALL_BY_PERIOD,
        _target: null,
        agent,
        since,
        type: types.CONVERSATION_LOADED_ALL_BY_PERIOD,
    };
}


/**
 * I create a conversation loaded ongoing by customer event.
 *
 * :param customer: customer id
 * :param ids: conversations ids
 *
 * :returns: event attributes
 */
function conversationLoadedOngoingByCustomer (
    customer: number | string,
    ids: number[],
): Events.IConversationLoadedOngoingByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADED_ONGOING_BY_CUSTOMER,
        _target: null,
        customer,
        ids,
        type: types.CONVERSATION_LOADED_ONGOING_BY_CUSTOMER,
    };
}


/**
 * I create a conversation loading event.
 *
 * :returns: conversation loading event
 */
const conversationLoading: IEventCreator<Events.IConversationLoading> =
    (): Events.IConversationLoading => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADING,
        _target: null,
        type: types.CONVERSATION_LOADING,
    });


/**
 * I create a conversation loading by customer event.
 *
 * :param customer: customer id
 *
 * :returns: conversation loading by customer event
 */
function conversationLoadingByCustomer (
    customer: string | number,
): Events.IConversationLoadingByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADING_BY_CUSTOMER,
        _target: null,
        customer,
        type: types.CONVERSATION_LOADING_BY_CUSTOMER,
    };
}


/**
 * I create a conversation loading by period event.
 *
 * :param agent: agent id
 *
 * :returns: conversation loading by period event
 */
function conversationLoadingByPeriod (
    agent: string,
): Events.IConversationLoadingByPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADING_BY_PERIOD,
        _target: null,
        agent,
        type: types.CONVERSATION_LOADING_BY_PERIOD,
    };
}


/**
 * I create a conversation loading ongoing by customer event.
 *
 * :returns: event attributes
 */
function conversationLoadingOngoingByCustomer
(): Events.IConversationLoadingOngoingByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_LOADING_ONGOING_BY_CUSTOMER,
        _target: null,
        type: types.CONVERSATION_LOADING_ONGOING_BY_CUSTOMER,
    };
}


/**
 * I create a conversation message ack received event.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: received ack
 *
 * :returns: conversation message ack received event
 */
function conversationMessageAckReceived (
    conversationId: number,
    messageId: number,
    ack: AckTypes,
): Events.IConversationMessageAckReceived
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_ACK_RECEIVED,
        _target: conversationId,
        ack,
        id: messageId,
        type: types.CONVERSATION_MESSAGE_ACK_RECEIVED,
    };
}


/**
 * I create a conversation message by original id fetched event.
 *
 * :param conversation: conversation id of replying message
 * :param message: message id
 * :param data: message data
 *
 * :returns: conversation message by original id fetched event
 */
function conversationMessageByOriginalIdFetched (
    conversation: number,
    message: number,
    data: ITimelineMessage,
): Events.IConversationMessageByOriginalIdFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_FETCHED,
        _target: conversation,
        data,
        message,
        type: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_FETCHED,
    };
}


/**
 * I create a conversation message by original id fetching event.
 *
 * :param conversation: conversation id of replying message
 * :param message: message id
 *
 * :returns: conversation message by original id fetching event
 */
function conversationMessageByOriginalIdFetching (
    conversation: number,
    message: number,
): Events.IConversationMessageByOriginalIdFetching
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_FETCHING,
        _target: conversation,
        message,
        type: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_FETCHING,
    };
}


/**
 * I create a conversation message by original id not fetched event.
 *
 * :param conversation: conversation id of replying message
 * :param message: message id
 *
 * :returns: conversation message by original id not fetched event
 */
function conversationMessageByOriginalIdNotFetched (
    conversation: number,
    message: number,
): Events.IConversationMessageByOriginalIdNotFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_NOT_FETCHED,
        _target: conversation,
        message,
        type: types.CONVERSATION_MESSAGE_BY_ORIGINAL_ID_NOT_FETCHED,
    };
}


/**
 * I create a message not sent event.
 *
 * :param id: conversation id
 * :param message: message not sent
 *
 * :returns: message not sent event
 */
const conversationMessageNotSent:
    IEventCreator<Events.IConversationMessageNotSent> =
    (id: number, message: IMessage): Events.IConversationMessageNotSent => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_NOT_SENT,
        _target: id,
        message: message,
        type: types.CONVERSATION_MESSAGE_NOT_SENT,
    });


/**
 * I create a conversation message original id received event.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param originalId: received original id
 *
 * :returns: conversation message original id received event
 */
function conversationMessageOriginalIdReceived (
    conversationId: number,
    messageId: number,
    originalId: string,
): Events.IConversationMessageOriginalIdReceived
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_ORIGINAL_ID_RECEIVED,
        _target: conversationId,
        id: messageId,
        originalId,
        type: types.CONVERSATION_MESSAGE_ORIGINAL_ID_RECEIVED,
    };
}


/*
 * I create a message received event.
 *
 * :param ack: message ack type
 * :param attachments: list of attachments
 * :param body: message body
 * :param caption: message caption
 * :param conversation: message conversation's id
 * :param deleted: message deleted timestamp
 * :param id: message id
 * :param messageClass: message class
 * :param messageType: message type
 * :param mode: message mode
 * :param name: message name
 * :param ongoing: updated ongoing array
 * :param originalId: message original id
 * :param referral: message referral
 * :param reply: original id of message being replied
 * :param timestamp: message timestamp
 * :param uid: message contact's uid
 * :param unansweredMessages: number of unanswered messages or nothing
 * :param outgoing: message outgoing
 *
 * :returns: message received event
 */
function conversationMessageReceived (
    ack: string,
    attachments: IAttachment[],
    body: string,
    caption: string,
    conversation: number,
    deleted: number | null,
    id: number,
    messageClass: string,
    messageType: string,
    mode: number | null,
    name: string,
    ongoing: (number | string)[],
    originalId: string,
    reducedBody: any,
    referral: IReferral | null,
    reply: string | null,
    timestamp: number,
    uid: string,
    unansweredMessages: number | null,
    outgoing: string | null = null,
): Events.IConversationMessageReceived
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_RECEIVED,
        _target: conversation,
        ack,
        attachments,
        body,
        caption,
        deleted,
        id,
        messageClass,
        messageType,
        mode,
        name,
        ongoing,
        originalId,
        outgoing,
        reducedBody,
        referral,
        reply,
        timestamp,
        type: types.CONVERSATION_MESSAGE_RECEIVED,
        uid,
        unansweredMessages,
    };
}


/**
 * I create a message retrying event.
 *
 * :param id: conversation id
 * :param message: message to be sent
 *
 * :returns: message retrying event
 */
const conversationMessageRetrying:
    IEventCreator<Events.IConversationMessageRetrying> =
    (id: number, message: IMessage): Events.IConversationMessageRetrying => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_RETRYING,
        _target: id,
        message,
        type: types.CONVERSATION_MESSAGE_RETRYING,
    });


/**
 * I create a message sending event.
 *
 * :param id: conversation id
 * :param message: message to be sent
 * :param ongoing: sorted ongoing customers
 *
 * :returns: message sending event
 */
function conversationMessageSending (
    id: number,
    message: any,
    ongoing: (number | string)[],
): Events.IConversationMessageSending
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_SENDING,
        _target: id,
        message,
        ongoing,
        type: types.CONVERSATION_MESSAGE_SENDING,
    };
}


/**
 * I create a message sent event.
 *
 * :param id: conversation id
 * :param message: message sent
 * :param oldOriginalId: message old original id
 *
 * :returns: message sent event
 */
function conversationMessageSent(
    id: number,
    message: IMessage,
    oldOriginalId: string,
): Events.IConversationMessageSent
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_MESSAGE_SENT,
        _target: id,
        message: message,
        oldOriginalId,
        type: types.CONVERSATION_MESSAGE_SENT,
    };
}


/**
 * I create a metrics fetched event.
 *
 * :param metrics: conversations metrics
 *
 * :returns: metrics fetched event
 */
function conversationMetricsFetched (
    metrics: IConversationsMetrics,
): Events.IConversationMetricsFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_METRICS_FETCHED,
        _target: null,
        metrics,
        type: types.CONVERSATION_METRICS_FETCHED,
    };
}


/**
 * I create a metrics fetching event.
 *
 * :returns: metrics fetching event
 */
function conversationMetricsFetching (): Events.IConversationMetricsFetching
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_METRICS_FETCHING,
        _target: null,
        type: types.CONVERSATION_METRICS_FETCHING,
    };
}


/**
 * I create a metrics not fetched event.
 *
 * :returns: metrics not fetched event
 */
function conversationMetricsNotFetched (): Events.IConversationMetricsNotFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_METRICS_NOT_FETCHED,
        _target: null,
        type: types.CONVERSATION_METRICS_NOT_FETCHED,
    };
}


/**
 * I create a metrics period set event.
 *
 * :param endDate: final timestamp
 * :param startDate: initial timestamp
 * :param filters: metrics filters
 *
 * :returns: metrics period set event
 */
function conversationMetricsPeriodSet (
    endDate: number,
    startDate: number,
    filters: IMetricsFilters,
): Events.IConversationMetricsPeriodSet
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_METRICS_PERIOD_SET,
        _target: null,
        endDate,
        filters,
        startDate,
        type: types.CONVERSATION_METRICS_PERIOD_SET,
    };
}


/**
 * I create a conversation not created event.
 *
 * :param error: error
 *
 * :returns: conversation not created event
 */
const conversationNotCreated: IEventCreator<Events.IConversationNotCreated> =
    (error: Error): Events.IConversationNotCreated => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_CREATED,
        _target: null,
        error: error,
        type: types.CONVERSATION_NOT_CREATED,
    });


/**
 * I create a conversation not fetched event.
 *
 * :param id: conversation id
 *
 * :returns: conversation not fetched event
 */
const conversationNotFetched: IEventCreator<Events.IConversationNotFetched> = (
    id: number,
): Events.IConversationNotFetched => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_NOT_FETCHED,
    _target: id,
    type: types.CONVERSATION_NOT_FETCHED,
});


/**
 * I create a conversation-not-fetched-by-ids event.
 *
 * :returns: conversation-not-fetched-by-ids event
 */
const conversationNotFetchedByIds: IEventCreator<
    Events.IConversationNotFetchedByIds
> = (): Events.IConversationNotFetchedByIds => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_NOT_FETCHED_BY_IDS,
    _target: null,
    type: types.CONVERSATION_NOT_FETCHED_BY_IDS,
});


/**
 * I create a conversation not finished event.
 *
 * :returns: conversation not finished event
 */
function conversationNotFinished (): Events.IConversationNotFinished
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_FINISHED,
        _target: null,
        type: types.CONVERSATION_NOT_FINISHED,
    };
}


/**
 * I create a conversation not loaded event.
 *
 * :returns: conversation not loaded event
 */
const conversationNotLoaded: IEventCreator<Events.IConversationNotLoaded> =
    (): Events.IConversationNotLoaded => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_LOADED,
        _target: null,
        type: types.CONVERSATION_NOT_LOADED,
    });


/**
 * I create a conversation not loaded by customer event.
 *
 * :param customer: customer id
 *
 * :returns: conversation not loaded by customer event
 */
function conversationNotLoadedByCustomer (
    customer: number | string,
): Events.IConversationNotLoadedByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_LOADED_BY_CUSTOMER,
        _target: null,
        customer,
        type: types.CONVERSATION_NOT_LOADED_BY_CUSTOMER,
    };
}


/**
 * I create a conversation not loaded by period event.
 *
 * :param agent: agent id
 *
 * :returns: conversation not loaded by period event
 */
function conversationNotLoadedByPeriod (
    agent: string,
): Events.IConversationNotLoadedByPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_LOADED_BY_PERIOD,
        _target: null,
        agent,
        type: types.CONVERSATION_NOT_LOADED_BY_PERIOD,
    };
}


/**
 * I create a conversation not loaded ongoing by customer event.
 *
 * :returns: event attributes
 */
function conversationNotLoadedOngoingByCustomer
(): Events.IConversationNotLoadedOngoingByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_LOADED_ONGOING_BY_CUSTOMER,
        _target: null,
        type: types.CONVERSATION_NOT_LOADED_ONGOING_BY_CUSTOMER,
    };
}


/**
 * I create a conversation not transferred event.
 *
 * :param id: conversation id
 * :param to: receiver agent uid
 * :param session: requester agent session token
 *
 * :returns: conversation not transferred event
 */
function conversationNotTransferred (
    id: number,
    to: string,
    session: string,
): Events.IConversationNotTransferred
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_NOT_TRANSFERRED,
        _target: id,
        session,
        to,
        type: types.CONVERSATION_NOT_TRANSFERRED,
    };
}


/**
 * I create a conversation online agent added event.
 *
 * :param fullname: agent full name
 * :param uid: agent uid
 *
 * :returns: conversation agent added event
 */
function conversationOnlineAgentAdded (
    agent: IAgentSession,
): Events.IConversationOnlineAgentAdded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ONLINE_AGENT_ADDED,
        _target: null,
        agent,
        type: types.CONVERSATION_ONLINE_AGENT_ADDED,
    };
}


/**
 * I create a conversation online agents loaded event.
 *
 * :returns: conversation online agents loaded event
 */
function conversationOnlineAgentsLoaded
(): Events.IConversationOnlineAgentsLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ONLINE_AGENTS_LOADED,
        _target: null,
        type: types.CONVERSATION_ONLINE_AGENTS_LOADED,
    };
}


/**
 * I create a conversation online agents loading event.
 *
 * :returns: conversation online agents loading event
 */
function conversationOnlineAgentsLoading(): IConversationOnlineAgentsLoading
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ONLINE_AGENTS_LOADING,
        _target: null,
        type: types.CONVERSATION_ONLINE_AGENTS_LOADING,
    };
}


/**
 * I create a conversation order prioritized event.
 *
 * :param history: customers order in history
 * :param ongoing: customers order in ongoing
 *
 * :returns: conversation order prioritized event
 */
function conversationOrderPrioritized(
    history: (number | string)[],
    ongoing: (number | string)[],
): Events.IConversationOrderPrioritized
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ORDER_PRIORITIZED,
        _target: null,
        history,
        ongoing,
        type: types.CONVERSATION_ORDER_PRIORITIZED,
    };
}


/**
 * I create a conversation online agents not loaded event.
 *
 * :returns: conversation online agents not loaded event
 */
function conversationOnlineAgentsNotLoaded
(): Events.IConversationOnlineAgentsNotLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_ONLINE_AGENTS_NOT_LOADED,
        _target: null,
        type: types.CONVERSATION_ONLINE_AGENTS_NOT_LOADED,
    };
}


/**
 * I create a conversation page loaded event.
 *
 * :param history: updated history
 * :param page: next page to load conversations
 *
 * :returns: conversation page loaded event
 */
function conversationPageLoaded(
    history: (number | string)[],
    page: number,
): Events.IConversationPageLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_PAGE_LOADED,
        _target: null,
        history,
        page,
        type: types.CONVERSATION_PAGE_LOADED,
    };
}


/**
 * I create a conversation page loading event.
 *
 * :returns: conversation page loading event
 */
function conversationPageLoading(): Events.IConversationPageLoading
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_PAGE_LOADING,
        _target: null,
        type: types.CONVERSATION_PAGE_LOADING,
    };
}


/**
 * I create a conversation page not loaded event.
 *
 * :returns: conversation page not loaded event
 */
function conversationPageNotLoaded(): Events.IConversationPageNotLoaded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_PAGE_NOT_LOADED,
        _target: null,
        type: types.CONVERSATION_PAGE_NOT_LOADED,
    };
}


/**
 * I create a conversation quality metadata set event.
 *
 * :param metadata: quality report metadata
 *
 * :returns: conversation quality metadata set event
 */
function conversationQualityMetadataSet(
    metadata: IQualityMetadata,
): Events.IConversationQualityMetadataSet
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_METADATA_SET,
        _target: null,
        metadata,
        type: types.CONVERSATION_QUALITY_METADATA_SET,
    };
}


/**
 * I create a conversation quality report fetched event.
 *
 * :param byId: quality conversations by id
 * :param endDate: end date
 * :param startDate: start date
 *
 * :returns: conversation quality report fetched event
 */
function conversationQualityReportFetched(
    byId: Record<number, IQualityConversation>,
    endDate: number,
    startDate: number,
): Events.IConversationQualityReportFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_REPORT_FETCHED,
        _target: null,
        byId,
        endDate,
        startDate,
        type: types.CONVERSATION_QUALITY_REPORT_FETCHED,
    };
}


/**
 * I create a conversation quality report fetching event.
 *
 * :returns: conversation quality report fetching event
 */
function conversationQualityReportFetching():
    Events.IConversationQualityReportFetching
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_REPORT_FETCHING,
        _target: null,
        type: types.CONVERSATION_QUALITY_REPORT_FETCHING,
    };
}


/**
 * I create a conversation quality report not fetched event.
 *
 * :returns: conversation quality report not fetched event
 */
function conversationQualityReportNotFetched(
    code: string | null,
): Events.IConversationQualityReportNotFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_REPORT_NOT_FETCHED,
        _target: null,
        code,
        type: types.CONVERSATION_QUALITY_REPORT_NOT_FETCHED,
    };
}


/**
 * I create a conversation quality report stopped event.
 *
 * :returns: conversation quality report stopped event
 */
function conversationQualityReportStopped():
    Events.IConversationQualityReportStopped
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_REPORT_STOPPED,
        _target: null,
        type: types.CONVERSATION_QUALITY_REPORT_STOPPED,
    };
}


/**
 * I create a conversation quality analyses expired event.
 *
 * :param conversations: conversation ids that got no event in time
 *
 * :returns: conversation quality analyses expired event
 */
function conversationQualityAnalysesExpired(
    conversations: number[],
): Events.IConversationQualityAnalysesExpired
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYSES_EXPIRED,
        _target: null,
        conversations,
        type: types.CONVERSATION_QUALITY_ANALYSES_EXPIRED,
    };
}


/**
 * I create a conversation quality analysis dispatched event.
 *
 * :param alreadyAnalyzed: conversations that got a score meanwhile
 * :param dispatched: conversations being analyzed
 * :param rejected: conversations the backend refused, with the reason
 *
 * :returns: conversation quality analysis dispatched event
 */
function conversationQualityAnalysisDispatched(
    alreadyAnalyzed: IAnalyzedConversation[],
    dispatched: number[],
    rejected: IRejectedConversation[],
): Events.IConversationQualityAnalysisDispatched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYSIS_DISPATCHED,
        _target: null,
        alreadyAnalyzed,
        dispatched,
        rejected,
        type: types.CONVERSATION_QUALITY_ANALYSIS_DISPATCHED,
    };
}


/**
 * I create a conversation quality analysis failed event.
 *
 * :param code: error code (http status as string)
 *
 * :returns: conversation quality analysis failed event
 */
function conversationQualityAnalysisFailed(
    code: string,
): Events.IConversationQualityAnalysisFailed
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYSIS_FAILED,
        _target: null,
        code,
        type: types.CONVERSATION_QUALITY_ANALYSIS_FAILED,
    };
}


/**
 * I create a conversation quality analysis finished event.
 *
 * :returns: conversation quality analysis finished event
 */
function conversationQualityAnalysisFinished():
    Events.IConversationQualityAnalysisFinished
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYSIS_FINISHED,
        _target: null,
        type: types.CONVERSATION_QUALITY_ANALYSIS_FINISHED,
    };
}


/**
 * I create a conversation quality analysis started event.
 *
 * :returns: conversation quality analysis started event
 */
function conversationQualityAnalysisStarted():
    Events.IConversationQualityAnalysisStarted
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYSIS_STARTED,
        _target: null,
        type: types.CONVERSATION_QUALITY_ANALYSIS_STARTED,
    };
}


/**
 * I create a conversation quality analyzed event.
 *
 * :param conversation: conversation id
 * :param score: score, only when analyzed
 * :param status: analyzed or failed
 *
 * :returns: conversation quality analyzed event
 */
function conversationQualityAnalyzed(
    conversation: number,
    score: number | null,
    status: 'analyzed' | 'failed',
): Events.IConversationQualityAnalyzed
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_QUALITY_ANALYZED,
        _target: conversation,
        conversation,
        score,
        status,
        type: types.CONVERSATION_QUALITY_ANALYZED,
    };
}


/**
 * I create a real time metrics fetched event.
 *
 * :param metrics: real Time metrics
 *
 * :returns: real time metrics fetched event
 */
function conversationRealTimeMetricsFetched (
    metrics: IRealTimeMetrics,
): Events.IConversationRealTimeMetricsFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_REALTIME_METRICS_FETCHED,
        _target: null,
        metrics,
        type: types.CONVERSATION_REALTIME_METRICS_FETCHED,
    };
}


/**
 * I create a real time metrics fetching event.
 *
 * :returns: real time metrics fetching event
 */
function conversationRealTimeMetricsFetching
(): Events.IConversationRealTimeMetricsFetching
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_REALTIME_METRICS_FETCHING,
        _target: null,
        type: types.CONVERSATION_REALTIME_METRICS_FETCHING,
    };
}


/**
 * I create a real time metrics not fetched event.
 *
 * :returns: real time metrics not fetched event
 */
function conversationRealTimeMetricsNotFetched
(): Events.IConversationRealTimeMetricsNotFetched
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_REALTIME_METRICS_NOT_FETCHED,
        _target: null,
        type: types.CONVERSATION_REALTIME_METRICS_NOT_FETCHED,
    };
}


/**
 * I create a conversation removed event.
 *
 * :param id: conversation id
 * :param history: updated history array
 * :param ongoing: updated ongoing array
 *
 * :returns: conversation removed event
 */
function conversationRemoved (
    id: number,
    history: (number | string)[],
    ongoing: (number | string)[],
): Events.IConversationRemoved
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_REMOVED,
        _target: id,
        history,
        ongoing,
        type: types.CONVERSATION_REMOVED,
    };
}


/**
 * I create a conversation session closed event.
 *
 * :returns: conversation session closed event
 */
function conversationSessionClosed (): Events.IConversationSessionClosed
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_SESSION_CLOSED,
        _target: null,
        type: types.CONVERSATION_SESSION_CLOSED,
    };
}


/**
 * I create a conversation session forcibly ended event.
 *
 * :returns: conversation session forcibly ended event
 */
function conversationSessionForciblyEnded
(): Events.IConversationSessionForciblyEnded
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_SESSION_FORCIBLY_ENDED,
        _target: null,
        type: types.CONVERSATION_SESSION_FORCIBLY_ENDED,
    };
}


/**
 * I create a conversation session not opened event.
 *
 * :returns: conversation session not opened event
 */
function conversationSessionNotOpened (): Events.IConversationSessionNotOpened
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_SESSION_NOT_OPENED,
        _target: null,
        type: types.CONVERSATION_SESSION_NOT_OPENED,
    };
}


/**
 * I create a conversation session opened event.
 *
 * :param session: session token
 *
 * :returns: conversation session opened event
 */
function conversationSessionOpened (
    push: IPush,
    session: string,
): Events.IConversationSessionOpened
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_SESSION_OPENED,
        _target: null,
        push,
        session,
        type: types.CONVERSATION_SESSION_OPENED,
    };
}


/**
 * I create a conversation should exit event.
 *
 * :param conversation: conversation id
 *
 * :returns: conversation should exit event
 */
function conversationShouldExit(
    conversation: number,
): Events.IConversationShouldExit
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_SHOULD_EXIT,
        _target: conversation,
        type: types.CONVERSATION_SHOULD_EXIT,
    };
}


/**
 * I create a conversation started event.
 *
 * :returns: conversation started event
 */
const conversationStarted: IEventCreator<Events.IConversationStarted> =
    (): Events.IConversationStarted => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_STARTED,
        _target: null,
        type: types.CONVERSATION_STARTED,
    });


/**
 * I create a conversation stop-load-by-customer event.
 *
 * :returns: conversation stop-load-by-customer event
 */
const conversationStopLoadByCustomer: IEventCreator<
    Events.IConversationStopLoadByCustomer
> = (): Events.IConversationStopLoadByCustomer => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: types.CONVERSATION_STOP_LOAD_BY_CUSTOMER,
    _target: null,
    type: types.CONVERSATION_STOP_LOAD_BY_CUSTOMER,
});


/**
 * I create a conversation stopped event.
 *
 * :returns: conversation stopped event
 */
const conversationStopped: IEventCreator<Events.IConversationStopped> =
    (): Events.IConversationStopped => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_STOPPED,
        _target: null,
        type: types.CONVERSATION_STOPPED,
    });


/**
 * I create a template not sent event.
 *
 * :param id: conversation id
 * :param originalId: message original id
 *
 * :returns: template not sent event
 */
function conversationTemplateNotSent(
    id: number,
    originalId: string,
): Events.IConversationTemplateNotSent
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TEMPLATE_NOT_SENT,
        _target: id,
        originalId: originalId,
        type: types.CONVERSATION_TEMPLATE_NOT_SENT,
    };
}


/**
 * I create a template sending event.
 *
 * :param id: conversation id
 * :param template: template information
 * :returns: template sending event
 */
function conversationTemplateSending(
    id: number,
    template: IMessage,
): Events.IConversationTemplateSending
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TEMPLATE_SENDING,
        _target: id,
        payload: template,
        type: types.CONVERSATION_TEMPLATE_SENDING,
    };
}


/**
 * I create a template sent event.
 *
 * :param id: conversation id
 * :param oldOriginalId: original id to be replaced
 * :param payload: template message information
 *
 * :returns: template sent event
 */
function conversationTemplateSent(
    id: number,
    oldOriginalId: string,
    payload: IMessage,
): Events.IConversationTemplateSent
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TEMPLATE_SENT,
        _target: id,
        oldOriginalId: oldOriginalId,
        payload: payload,
        type: types.CONVERSATION_TEMPLATE_SENT,
    };
}


/**
 * I create a conversation transferred event.
 *
 * :param id: conversation id
 * :param to: receiver agent uid
 * :param session: requester agent session token
 *
 * :returns: conversation transferred event
 */
function conversationTransferred (
    id: number,
    to: string,
    session: string,
): Events.IConversationTransferred
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TRANSFERRED,
        _target: id,
        session,
        to,
        type: types.CONVERSATION_TRANSFERRED,
    };
}


/**
 * I create a conversation transferring event.
 *
 * :param id: conversation id
 * :param to: receiver agent uid
 * :param session: requester agent session token
 *
 * :returns: conversation transferring event
 */
function conversationTransferring (
    id: number,
    to: string,
    session: string,
): Events.IConversationTransferring
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TRANSFERRING,
        _target: id,
        session,
        to,
        type: types.CONVERSATION_TRANSFERRING,
    };
}


/**
 * I create a conversation transfer reset event.
 *
 * :returns: conversation transfer reset event
 */
function conversationTransferReset (): Events.IConversationTransferReset
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TRANSFER_RESET,
        _target: null,
        type: types.CONVERSATION_TRANSFER_RESET,
    };
}


/**
 * I create a conversation typed text event.
 *
 * :param id: conversation id
 * :param text: typed message text
 *
 * :returns: conversation typed text event
 */
function conversationTypedText (
    id: number,
    text: string,
): Events.IConversationTypedText
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_TYPED_TEXT,
        _target: id,
        text,
        type: types.CONVERSATION_TYPED_TEXT,
    };
}


/**
 * I create an unsupported message sent event.
 *
 * :param body: message body
 * :param message: message sent
 * :param originalId: message original id
 *
 * :returns: unsupported message sent event
 */
function conversationUnsupportedMessageSent (
    body: string,
    conversation: number,
    originalId: string,
): Events.IConversationUnsupportedMessageSent
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_UNSUPPORTED_MESSAGE_SENT,
        _target: conversation,
        body,
        originalId,
        type: types.CONVERSATION_UNSUPPORTED_MESSAGE_SENT,
    };
}


/**
 * I create a conversation with another agent event.
 *
 * :param agent: agent name
 *
 * :returns: conversation with another agent event
 */
const conversationWithAnotherAgent:
    IEventCreator<Events.IConversationWithAnotherAgent> =
    (agent: string): Events.IConversationWithAnotherAgent => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_WITH_ANOTHER_AGENT,
        _target: null,
        agent: agent,
        type: types.CONVERSATION_WITH_ANOTHER_AGENT,
    });


/**
 * I create a conversation with another app event.
 *
 * :param app: app name
 *
 * :returns: conversation with another app event
 */
const conversationWithAnotherApp:
    IEventCreator<Events.IConversationWithAnotherApp> =
    (app: string): Events.IConversationWithAnotherApp => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: types.CONVERSATION_WITH_ANOTHER_APP,
        _target: null,
        app,
        type: types.CONVERSATION_WITH_ANOTHER_APP,
    });


/**
 * EXPORTS
 */
export {
    conversationAdded,
    conversationAgentsMessagesMetricsFetched,
    conversationAgentsMessagesMetricsFetching,
    conversationAgentsMessagesMetricsNotFetched,
    conversationAllPagesLoaded,
    conversationAlreadyExists,
    conversationAutoAnswered,
    conversationCreated,
    conversationCreating,
    conversationErrorReset,
    conversationExited,
    conversationFeedbackAdded,
    conversationFeedbackRemoved,
    conversationFetched,
    conversationFetchedByIds,
    conversationFetching,
    conversationFetchingByIds,
    conversationFileNotUploaded,
    conversationFileUploaded,
    conversationFileUploading,
    conversationFinished,
    conversationFinishing,
    conversationFocused,
    conversationForwardStarted,
    conversationForwardStopped,
    conversationForwardingMessageAdded,
    conversationForwardingMessageRemoved,
    conversationHeartbeatSucceeded,
    conversationHistoryAdded,
    conversationLoaded,
    conversationLoadedAllByPeriod,
    conversationLoadedByCustomer,
    conversationLoadedByPeriod,
    conversationLoadedOngoingByCustomer,
    conversationLoading,
    conversationLoadingByCustomer,
    conversationLoadingByPeriod,
    conversationLoadingOngoingByCustomer,
    conversationMessageAckReceived,
    conversationMessageByOriginalIdFetched,
    conversationMessageByOriginalIdFetching,
    conversationMessageByOriginalIdNotFetched,
    conversationMessageNotSent,
    conversationMessageOriginalIdReceived,
    conversationMessageReceived,
    conversationMessageRetrying,
    conversationMessageSending,
    conversationMessageSent,
    conversationMetricsFetched,
    conversationMetricsFetching,
    conversationMetricsNotFetched,
    conversationMetricsPeriodSet,
    conversationNotCreated,
    conversationNotFetched,
    conversationNotFetchedByIds,
    conversationNotFinished,
    conversationNotLoaded,
    conversationNotLoadedByCustomer,
    conversationNotLoadedByPeriod,
    conversationNotLoadedOngoingByCustomer,
    conversationNotTransferred,
    conversationOnlineAgentAdded,
    conversationOnlineAgentsLoaded,
    conversationOnlineAgentsLoading,
    conversationOnlineAgentsNotLoaded,
    conversationOrderPrioritized,
    conversationPageLoaded,
    conversationPageLoading,
    conversationPageNotLoaded,
    conversationQualityAnalysesExpired,
    conversationQualityAnalysisDispatched,
    conversationQualityAnalysisFailed,
    conversationQualityAnalysisFinished,
    conversationQualityAnalysisStarted,
    conversationQualityAnalyzed,
    conversationQualityMetadataSet,
    conversationQualityReportFetched,
    conversationQualityReportFetching,
    conversationQualityReportNotFetched,
    conversationQualityReportStopped,
    conversationRealTimeMetricsFetched,
    conversationRealTimeMetricsFetching,
    conversationRealTimeMetricsNotFetched,
    conversationRemoved,
    conversationSessionClosed,
    conversationSessionForciblyEnded,
    conversationSessionNotOpened,
    conversationSessionOpened,
    conversationShouldExit,
    conversationStarted,
    conversationStopLoadByCustomer,
    conversationStopped,
    conversationTemplateNotSent,
    conversationTemplateSending,
    conversationTemplateSent,
    conversationTransferred,
    conversationTransferReset,
    conversationTransferring,
    conversationTypedText,
    conversationUnsupportedMessageSent,
    conversationWithAnotherAgent,
    conversationWithAnotherApp,
};
